import * as XLSX from 'xlsx'
import store from '@/store'

export default function useExportGAP() {
  const { client } = store.state?.userStore?.userData
  const clientData = JSON.parse(localStorage.getItem("clientData") || '{}')
  const clientName = clientData?.name || client?.name || 'Cliente'

  const exportToExcel = (gaps) => {
    if (!gaps) {
      return
    }

    // Create workbook
    const wb = XLSX.utils.book_new()

    // Add GAP sheet with gaps
    addGAPSheet(wb, gaps)

    // Generate filename
    const today = new Date().toISOString().split('T')[0]
    const filename = `GAP_Ley21719_${today}.xlsx`
    XLSX.writeFile(wb, filename)
  }

  const addGAPSheet = (wb, gaps) => {
    // Fila 1: Título principal (A1:L1)
    const headerRow1 = [
      'ANÁLISIS DE BRECHAS - LEY 21.719', // A1
      ...Array(11).fill('') // B1-L1 (merged with A1)
    ]

    // Fila 2: Secciones principales
    const headerRow2 = [
      'OBLIGACIÓN', // A2
      '', '', // B2-C2 (merged with A2)
      'EVALUACIÓN', // D2
      '', // E2 (merged with D2)
      'BRECHA IDENTIFICADA', // F2
      '', '', // G2-H2 (merged with F2)
      'PLAN DE ACCIÓN', // I2
      '', '', '' // J2-L2 (merged with I2)
    ]

    // Fila 3: Títulos individuales de cada columna
    const headerRow3 = [
      'N°', // A3
      'Obligación', // B3
      'Artículo', // C3
      'Estado', // D3
      'Evidencia', // E3
      'Descripción de la Brecha', // F3
      'Plan de Acción', // G3
      'Responsable', // H3
      'Plazo', // I3
      'Estado del Plan', // J3
    ]

    // Build data rows from gaps
    const dataRows = gaps.map(gap => {
      return [
        gap.obligationOrder || '', // A: N°
        gap.obligationName || '', // B: Obligación
        gap.obligationArticle || '', // C: Artículo
        gap.status || 'No implementado', // D: Estado
        gap.evidence || '', // E: Evidencia
        gap.gapDescription || '', // F: Descripción de la Brecha
        gap.actionPlan || '', // G: Plan de Acción
        gap.responsibleName || '', // H: Responsable
        formatDate(gap.deadline), // I: Plazo
        gap.planStatus || '', // J: Estado del Plan
      ]
    })

    // Create worksheet with headers (3 header rows + data)
    const wsData = [headerRow1, headerRow2, headerRow3, ...dataRows]
    const ws = XLSX.utils.aoa_to_sheet(wsData)

    // Set column widths (A-L = 12 columns)
    ws['!cols'] = [
      { wch: 8 },   // A: N°
      { wch: 50 },  // B: Obligación
      { wch: 15 },  // C: Artículo
      { wch: 18 },  // D: Estado
      { wch: 40 },  // E: Evidencia
      { wch: 50 },  // F: Descripción de la Brecha
      { wch: 50 },  // G: Plan de Acción
      { wch: 25 },  // H: Responsable
      { wch: 12 },  // I: Plazo
      { wch: 15 },  // J: Estado del Plan
      { wch: 25 },  // K: Empresa
      { wch: 25 }   // L: ID Negocio
    ]

    // Add row heights
    if (!ws['!rows']) ws['!rows'] = []
    ws['!rows'][0] = { hpt: 25 } // Header row 1
    ws['!rows'][1] = { hpt: 25 } // Header row 2
    ws['!rows'][2] = { hpt: 35 } // Header row 3 (column titles)

    // Style header rows with background color
    const headerRange = XLSX.utils.decode_range(ws['!ref'])
    for (let R = 0; R <= 2; R++) {
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: R, c: C })
        if (!ws[address]) continue
        ws[address].s = {
          fill: { fgColor: { rgb: '2F5496' } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
          }
        }
      }
    }

    // Apply color coding to status cells (column D, starting from row 4)
    for (let R = 3; R < wsData.length; R++) {
      const statusAddress = XLSX.utils.encode_cell({ r: R, c: 3 }) // Column D
      if (!ws[statusAddress]) continue

      const status = ws[statusAddress].v
      let bgColor = 'FFFFFF' // default white

      switch (status) {
        case 'Implementado':
          bgColor = '70AD47' // verde
          break
        case 'Parcial':
          bgColor = 'FFC000' // amarillo
          break
        case 'No implementado':
          bgColor = 'C00000' // rojo
          break
        case 'No aplica':
          bgColor = 'CCCCCC' // gris
          break
      }

      ws[statusAddress].s = {
        fill: { fgColor: { rgb: bgColor } },
        font: { bold: true, color: { rgb: status === 'No aplica' ? '000000' : 'FFFFFF' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      }
    }

    // Merge cells for header groups
    if (!ws['!merges']) ws['!merges'] = []

    // Fila 1: ANÁLISIS DE BRECHAS - LEY 21.719 (A1:L1)
    ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 11 } })

    // Fila 2: OBLIGACIÓN (A2:C2)
    ws['!merges'].push({ s: { r: 1, c: 0 }, e: { r: 1, c: 2 } })

    // Fila 2: EVALUACIÓN (D2:E2)
    ws['!merges'].push({ s: { r: 1, c: 3 }, e: { r: 1, c: 4 } })

    // Fila 2: BRECHA IDENTIFICADA (F2:H2)
    ws['!merges'].push({ s: { r: 1, c: 5 }, e: { r: 1, c: 7 } })

    // Fila 2: PLAN DE ACCIÓN (I2:L2)
    ws['!merges'].push({ s: { r: 1, c: 8 }, e: { r: 1, c: 11 } })

    XLSX.utils.book_append_sheet(wb, ws, 'GAP')
  }

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return ''
    const d = new Date(date)
    const year = d.getUTCFullYear()
    const month = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${day}-${month}-${year}`
  }

  return {
    exportToExcel
  }
}
